import {
  resolveUniqueAssemblyOfflinePackageFile,
  type AssemblyOfflinePackageFileLike,
  type ModelAssetNode,
} from '@puppycad/cad-core'
import type { CadNode } from '@puppycad/proto'
import { CadFileKind } from '@puppycad/proto'

const ASSEMBLY_MANIFEST_FILE_ID = '_assembly.json'

/**
 * 普通节点不参与装配树扩展。
 */
export interface PassthroughBackendTreeExpansionPolicy {
  /** 策略标识。 */
  readonly kind: 'passthrough'
}

/**
 * 当前装配加载单元由唯一 `.puppy` 离线包承载。
 */
export interface OfflinePackageAuthoritativeBackendTreeExpansionPolicy<
  TFile extends AssemblyOfflinePackageFileLike,
> {
  /** 策略标识。 */
  readonly kind: 'offline-package-authoritative'
  /** 当前加载单元唯一的装配 `.puppy` 离线包。 */
  readonly packageFile: TFile
}

/**
 * 当前装配加载单元继续使用已有 children 树。
 */
export interface ChildrenTreeBackendTreeExpansionPolicy {
  /** 策略标识。 */
  readonly kind: 'children-tree'
}

/**
 * 当前装配加载单元缺少 children，允许在入口处补读一次后端树。
 */
export interface ReloadMissingChildrenBackendTreeExpansionPolicy {
  /** 策略标识。 */
  readonly kind: 'reload-missing-children'
}

/**
 * 后端装配树扩展策略。
 */
export type ProjectModelBackendTreeExpansionPolicy<TFile extends AssemblyOfflinePackageFileLike> =
  | PassthroughBackendTreeExpansionPolicy
  | OfflinePackageAuthoritativeBackendTreeExpansionPolicy<TFile>
  | ChildrenTreeBackendTreeExpansionPolicy
  | ReloadMissingChildrenBackendTreeExpansionPolicy

/**
 * 解析后端树扩展策略的通用输入。
 */
export interface ResolveProjectModelBackendTreeExpansionPolicyInput<
  TFile extends AssemblyOfflinePackageFileLike,
> {
  /** 当前加载单元是否应按装配根处理。 */
  readonly isAssemblyRoot: boolean
  /** 当前加载单元可用于装配离线包判断的文件集合。 */
  readonly files: readonly TFile[]
  /** 错误上下文中的仓库 ID。 */
  readonly repositoryId?: string
  /** 错误上下文中的提交 ID。 */
  readonly commitId?: string
  /** 当前加载单元已有的直接 children 数量。 */
  readonly childCount: number
  /** 调用方是否具备补读后端树的能力。 */
  readonly canReloadChildren: boolean
}

/**
 * 判断后端 CadNode 是否携带装配恢复清单。
 *
 * @param rootNode 后端返回的 CAD 节点。
 * @returns 节点文件列表是否包含 `_assembly.json`。
 */
export function hasCadNodeAssemblyManifestFile(rootNode: CadNode): boolean {
  return rootNode.files.some((file) => file.fileId === ASSEMBLY_MANIFEST_FILE_ID)
}

/**
 * 解析统一的后端装配树扩展策略。
 *
 * 设计说明：OpenProject 与 GetCadNodeTree 返回的加载单元必须共用同一条规则。
 * 只要当前装配根命中唯一 `.puppy`，主结构就不再直接消费 CadNode.children；
 * 但装配节点仍会在加载后继续对比该 root 自己的 `.puppy` 一级节点和 `_assembly.json` direct child。
 * 只有没有 `.puppy` 且缺少 children 时，入口层才允许补读完整后端树。
 *
 * @param input 当前加载单元的后端树扩展上下文。
 * @returns 可供 OpenProject、CadNode mapper 和加载准备器共用的策略。
 */
export function resolveProjectModelBackendTreeExpansionPolicy<
  TFile extends AssemblyOfflinePackageFileLike,
>(
  input: ResolveProjectModelBackendTreeExpansionPolicyInput<TFile>,
): ProjectModelBackendTreeExpansionPolicy<TFile> {
  if (!input.isAssemblyRoot) {
    return { kind: 'passthrough' }
  }

  const packageFile = resolveUniqueAssemblyOfflinePackageFile({
    files: input.files,
    repositoryId: input.repositoryId,
    commitId: input.commitId,
  })

  if (packageFile) {
    return {
      kind: 'offline-package-authoritative',
      packageFile,
    }
  }

  if (input.childCount === 0 && input.canReloadChildren) {
    return { kind: 'reload-missing-children' }
  }

  return { kind: 'children-tree' }
}

/**
 * 解析 OpenProject/GetCadNodeTree 返回的 CadNode 加载单元扩展策略。
 *
 * @param input CadNode 与补读能力。
 * @returns 当前 CadNode 的后端树扩展策略。
 */
export function resolveCadNodeBackendTreeExpansionPolicy(input: {
  readonly rootNode: CadNode
  readonly canReloadChildren: boolean
}): ProjectModelBackendTreeExpansionPolicy<CadNode['files'][number]> {
  const rootNode = input.rootNode
  const isAssemblyRoot = rootNode.type === CadFileKind.ASSEMBLY
    || hasCadNodeAssemblyManifestFile(rootNode)

  return resolveProjectModelBackendTreeExpansionPolicy({
    isAssemblyRoot,
    files: rootNode.files,
    repositoryId: rootNode.repositoryId.toString(),
    commitId: rootNode.commitId,
    childCount: rootNode.children.length,
    canReloadChildren: input.canReloadChildren,
  })
}

/**
 * 解析 cad-core 项目模型节点加载单元扩展策略。
 *
 * @param input 项目模型节点与补读能力。
 * @returns 当前 ModelAssetNode 的后端树扩展策略。
 */
export function resolveModelAssetNodeBackendTreeExpansionPolicy(input: {
  readonly node: ModelAssetNode
  readonly canReloadChildren: boolean
}): ProjectModelBackendTreeExpansionPolicy<ModelAssetNode['packageFiles'][number]> {
  const node = input.node

  return resolveProjectModelBackendTreeExpansionPolicy({
    isAssemblyRoot: node.nodeType === 'assembly',
    files: node.packageFiles,
    repositoryId: node.repositoryId,
    commitId: node.commitId,
    childCount: node.children.length,
    canReloadChildren: input.canReloadChildren,
  })
}
